import React, { type FC, type PropsWithChildren } from 'react';

import type { FieldRenderProps } from 'react-final-form';

import FieldWrapper from '../field-wrapper/field-wrapper';
import Combobox from '../../input/combobox/combobox';
import type { Lucid } from '../../../types/types';

export interface DropdownProps extends FieldRenderProps<Lucid.Marketo.FormFieldStateValues, any> {
  /** Allows passing additional styles to the component. */
  className?: string;
  /** The id that links the form input with the label. */
  id: string;
  /** Determines whether to use Dark Mode styles */
  isDarkMode?: boolean;
  /** Checks if the field is disabled. */
  isDisabled?: boolean;
  /** Checks if the field is hidden. */
  isHidden?: boolean;
  /** Checks if the field is required to submit. */
  isRequired?: boolean;
  /** Checks if the field is full width. */
  isFullWidth?: boolean;
  /** Determines whether the text should be light or dark */
  hasDarkBackground?: boolean;
  /** The options to include in the dropdown */
  options: Lucid.Marketo.Values[] | null | undefined;
  /** The label for the radio group */
  label: string;
  /** A custom error message for the input */
  customErrorMessage?: string;
}

const Dropdown: FC<PropsWithChildren<DropdownProps>> = ({
  className,
  customErrorMessage,
  hasDarkBackground,
  id,
  input,
  isDarkMode = false,
  isDisabled = false,
  isFullWidth = false,
  isHidden = false,
  isRequired = false,
  label,
  meta,
  options = [],
  ...rest
}) => {
  const hasError = meta?.error && meta?.touched;
  const errorMessage = customErrorMessage || meta?.error;
  const { value = '', onBlur, onChange, ...inputRest } = Object(input);

  const handleOnChange = (option: string) => {
    if (onChange) {
      onChange(option);
    }

    if (onBlur) {
      onBlur(option);
    }
  };

  const defaultValue = options?.find(({ isDefault }) => isDefault);

  return (
    <FieldWrapper
      isDropdown
      className={className}
      customErrorMessage={errorMessage}
      hasDarkBackground={hasDarkBackground}
      hasError={hasError}
      id={id}
      isDarkMode={isDarkMode}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      isHidden={isHidden}
      isRequired={isRequired}
      label={label}
      labelId={`${id}-label`}
    >
      <Combobox
        {...rest}
        aria-invalid={hasError}
        className={className}
        css={theme => [
          {
            backgroundColor: theme.colors.white,
          },
          isDarkMode && {
            backgroundColor: theme.colors.coolGrey[100],
            '& li:hover, & li[data-visual-focus="true"], & li:focus': {
              background: `${theme.colors.coolGrey[70]} !important`,
            },
            '& li[data-visual-focus="true"]': {
              borderColor: 'transparent',
              outline: '2px solid white',
              outlineOffset: '-3px',
            },
            '& svg > path': {
              stroke: theme.colors.white,
            },
            '*': {
              color: theme.colors.white,
            },
          },
        ]}
        id={id}
        onChange={optionSelected => handleOnChange(optionSelected.value)}
      >
        <Combobox.Box
          css={theme => [
            isRequired &&
              hasError &&
              !isDarkMode && {
                borderColor: theme.colors.pink[80],
                paddingTop: 'unset',
              },
            isDarkMode && {
              borderColor: theme.colors.white,
              '&:focus-within': {
                border: '2px solid white',
                outline: 'none',
                background: theme.colors.coolGrey[90],
              },
            },
          ]}
          onBlur={onBlur}
          variant="form"
        >
          <Combobox.Input
            {...inputRest}
            hasCarot
            aria-describedby={hasError ? `fieldErrorMessage-${id}` : undefined}
            aria-labelledby={`${id}-label`}
            startValue={defaultValue ? defaultValue.label : ''}
            value={Array.isArray(value) ? value[0] : value}
          />
        </Combobox.Box>
        <Combobox.List
          css={theme => [
            isDarkMode && {
              background: theme.colors.coolGrey[100],
              color: theme.colors.white,
            },
          ]}
          variant="form"
        >
          {options &&
            options.map(option => (
              <Combobox.Option key={option.value} text={option.label} value={option.value || ''} />
            ))}
        </Combobox.List>
      </Combobox>
    </FieldWrapper>
  );
};

export { Dropdown };
export default Dropdown;
